import { createFileRoute, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { LocaleLink } from "@/components/site/LocaleLink";
import { Section } from "@/components/site/Section";
import { Overline } from "@/components/site/Overline";
import { publishedMunicipalityQuery } from "@/lib/projects-queries";
import { projectTypeLabel } from "@/content/project-types";
import { canonical, langPageMeta, SITE_URL } from "@/content/site";

export const Route = createFileRoute("/$lang/realisaties/gemeente/$gemeente")({
  staticData: { sitemap: false },
  loader: async ({ context, params }) => {
    const result = await context.queryClient.ensureQueryData(publishedMunicipalityQuery(params.gemeente));
    if (!result) throw notFound();
    return result;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Gemeente niet gevonden" }, { name: "robots", content: "noindex" }] };
    const { municipality } = loaderData;
    const path = `/realisaties/gemeente/${municipality.slug}`;
    const title = `Zwembadprojecten in ${municipality.gemeente} | Gopro Projects`;
    const description = `Bekijk ${municipality.projectCount} gepubliceerde zwembadprojecten in ${municipality.gemeente}: nieuwbouw en renovatie door Gopro Projects uit Maasmechelen.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        ...langPageMeta(params.lang, path).meta,
        { name: "twitter:card", content: "summary" },
      ],
      links: langPageMeta(params.lang, path).links,
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: title,
          description,
          url: canonical(`/${params.lang}${path}`),
          about: { "@type": "Place", name: municipality.gemeente },
          provider: { "@id": `${SITE_URL}/#organization` },
        }),
      }],
    };
  },
  component: MunicipalityPage,
});

function MunicipalityPage() {
  const { gemeente } = Route.useParams();
  const { data } = useSuspenseQuery(publishedMunicipalityQuery(gemeente));
  if (!data) return null;
  const { municipality, projects } = data;
  return (
    <div>
      <Section tone="navy" className="pt-32 lg:pt-40">
        <Overline onDark>Realisaties per gemeente</Overline>
        <h1 className="display-hero mt-4">Zwembadprojecten in {municipality.gemeente}</h1>
        <p className="mt-6 max-w-[70ch] text-[17px] leading-7 text-primary-foreground/80">
          Bekijk onze gepubliceerde nieuwbouw- en renovatieprojecten in {municipality.gemeente}. We werken vanuit Maasmechelen in Limburg en Zuid-Nederland.
        </p>
      </Section>
      <Section tone="mist">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <LocaleLink
              key={project.id}
              to="/realisaties/$slug"
              params={{ slug: project.slug }}
              className="border border-border bg-background p-6 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <p className="font-display text-lg font-semibold">{project.titel}</p>
              <p className="mt-3 flex items-center gap-2 text-sm text-on-surface-variant">
                <MapPin className="h-5 w-5 text-brand-deep-water" strokeWidth={1.75} />
                {projectTypeLabel(project.typeWerk)} in {municipality.gemeente}
              </p>
              {project.antwoordSamenvatting || project.samenvatting ? (
                <p className="mt-4 text-sm leading-6 text-on-surface-variant">
                  {project.antwoordSamenvatting || project.samenvatting}
                </p>
              ) : null}
            </LocaleLink>
          ))}
        </div>
      </Section>
    </div>
  );
}